import { useEffect, useRef, useState } from 'react';

import {
    Box,
    FormControl,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Popover,
    Select,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import { IconSettings } from '@tabler/icons';

import useTwilioCall from 'hooks/useTwilioCall';

// Menu UX: this sentinel represents "system default" in the Select (the slice
// stores it as null). MUI Select treats `null`/`''` as "nothing selected" and
// renders an empty field with the floating label dropped, so we use a
// non-empty sentinel and translate sentinel ↔ null at the boundary.
const DEFAULT_VALUE = 'system-default';

/**
 * @param {{ deviceId: string, label: string }} device
 * @param {number} fallbackIndex
 * @param {string} kind  'Microphone' | 'Speaker' — used in the synthesized label
 * @returns {string}
 */
function deviceLabel(device, fallbackIndex, kind) {
    if (device.label) {
        return device.label;
    }
    return `${kind} ${fallbackIndex + 1}`;
}

// Mic level is only meaningful once Twilio has acquired the local input stream.
// device.connect() acquires it, and it stays live through ringing → inCall.
// Outside this window the meter would sit at zero, which reads as "broken mic"
// rather than "not yet listening" — so we hide it.
const MIC_ENGAGED_STATUSES = new Set(['connecting', 'ringing', 'inCall']);

const AudioDevicePicker = () => {
    const { status, audio, setInputDevice, setSpeakerDevice, getInputVolume } = useTwilioCall();
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);

    const micEngaged = MIC_ENGAGED_STATUSES.has(status);

    // Local mic-level state polled directly from the controller while the menu
    // is open AND the mic is engaged. Polled at ~20Hz; intentionally NOT pushed
    // through Redux to avoid re-rendering unrelated subscribers 20 times a second.
    const [inputVolume, setInputVolume] = useState(0);

    useEffect(() => {
        if (!open || !micEngaged) {
            setInputVolume(0);
            return undefined;
        }
        const interval = setInterval(() => {
            setInputVolume(getInputVolume());
        }, 50);
        return () => clearInterval(interval);
    }, [open, micEngaged, getInputVolume]);

    const onInputChange = (event) => {
        const value = event.target.value;
        setInputDevice(value === DEFAULT_VALUE ? null : value);
    };

    const onSpeakerChange = (event) => {
        const value = event.target.value;
        setSpeakerDevice(value === DEFAULT_VALUE ? null : value);
    };

    const inputValue = audio.selectedInputDeviceId ?? DEFAULT_VALUE;
    const outputValue = audio.selectedOutputDeviceId ?? DEFAULT_VALUE;

    // The Select's dropdown is a Modal-based Popover; without an explicit
    // zIndex it sits at zIndex.modal (1300), behind the call card and behind
    // this picker's own Popover. Push it above both.
    const selectMenuProps = { sx: { zIndex: (t) => t.zIndex.snackbar + 3 } };

    return (
        <>
            <Tooltip title="Audio devices">
                <IconButton ref={anchorRef} onClick={() => setOpen(true)} aria-label="Audio devices" size="small">
                    <IconSettings size={18} />
                </IconButton>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                // The call card itself sits at zIndex.snackbar + 1; the
                // Popover/Modal default is zIndex.modal (1300), which renders
                // behind the card. Push it above so the picker is visible.
                sx={{ zIndex: (t) => t.zIndex.snackbar + 2 }}
                PaperProps={{ sx: { p: 2, width: 300 } }}
            >
                <Stack spacing={2}>
                    <Box>
                        <FormControl fullWidth size="small">
                            <InputLabel id="twilio-mic-select-label">Microphone</InputLabel>
                            <Select
                                labelId="twilio-mic-select-label"
                                label="Microphone"
                                value={inputValue}
                                onChange={onInputChange}
                                MenuProps={selectMenuProps}
                            >
                                <MenuItem value={DEFAULT_VALUE}>System default</MenuItem>
                                {audio.inputDevices.map((d, i) => (
                                    <MenuItem key={d.deviceId} value={d.deviceId}>
                                        {deviceLabel(d, i, 'Microphone')}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {micEngaged && (
                            <Box sx={{ mt: 1 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.round(inputVolume * 100)}
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    Mic level
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {audio.outputSelectionSupported ? (
                        <FormControl fullWidth size="small">
                            <InputLabel id="twilio-speaker-select-label">Speaker</InputLabel>
                            <Select
                                labelId="twilio-speaker-select-label"
                                label="Speaker"
                                value={outputValue}
                                onChange={onSpeakerChange}
                                MenuProps={selectMenuProps}
                            >
                                <MenuItem value={DEFAULT_VALUE}>System default</MenuItem>
                                {audio.outputDevices.map((d, i) => (
                                    <MenuItem key={d.deviceId} value={d.deviceId}>
                                        {deviceLabel(d, i, 'Speaker')}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            Speaker follows your system default in this browser.
                        </Typography>
                    )}
                </Stack>
            </Popover>
        </>
    );
};

export default AudioDevicePicker;
